<template>
  <div class="ed-wx-cb">
    <p class="ed-wx-cb__msg">{{ message }}</p>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import type { AuthSession } from '../../../shared/components/authSessionStore'
import {
  clearOAuthPopupContext,
  postOAuthPopupFailure,
  postOAuthPopupSuccess,
  readOAuthPopupContext,
} from '../../../shared/utils/oauthPopup'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { useMockupEditorStore } from '@/stores/mockupEditor'
import { resolveMarketingApiOrigin } from '@/utils/marketingApiOrigin'
import { uiLocaleToEditorRouterPath } from '@/router/editorUrlLocale'

const message = ref('Connecting WeChat...')
const { setSession } = useEditorAuth()
const router = useRouter()
const store = useMockupEditorStore()

function buildKeepFields(keep: boolean, keepDays: number): Pick<AuthSession, 'keepDays' | 'expiresAt'> {
  if (!keep) return {}
  const days = [7, 14, 30].includes(keepDays) ? keepDays : 30
  return {
    keepDays: days,
    expiresAt: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
  }
}

async function finishWechatLoginSuccess(): Promise<void> {
  if (typeof globalThis.window === 'undefined') return
  await router.replace(uiLocaleToEditorRouterPath(store.locale))
}

onMounted(async () => {
  if (import.meta.env.SSR) return
  const q = new URLSearchParams(globalThis.location.search)
  const code = (q.get('code') || '').trim()
  const state = (q.get('state') || '').trim()
  const popupContext = readOAuthPopupContext()
  const saved = popupContext?.state || sessionStorage.getItem('wechat_oauth_state') || ''
  const redirectUri = popupContext?.redirectUri || sessionStorage.getItem('wechat_oauth_redirect_uri') || ''
  const keep = popupContext?.keep ?? (sessionStorage.getItem('wechat_oauth_keep') === '1')
  const keepDays = popupContext?.keepDays ?? (Number(sessionStorage.getItem('wechat_oauth_keep_days') || '0') || 0)

  if (!code || !state || !saved || state !== saved || !redirectUri) {
    message.value = 'WeChat authorization is invalid or expired. Please close this window and try again.'
    postOAuthPopupFailure('wechat', message.value)
    return
  }

  sessionStorage.removeItem('wechat_oauth_state')
  sessionStorage.removeItem('wechat_oauth_redirect_uri')
  sessionStorage.removeItem('wechat_oauth_keep')
  sessionStorage.removeItem('wechat_oauth_keep_days')
  clearOAuthPopupContext()

  const apiBase = resolveMarketingApiOrigin()
  if (!apiBase) {
    message.value = 'WeChat login failed: API base URL is missing.'
    postOAuthPopupFailure('wechat', message.value)
    return
  }

  try {
    const res = await fetch(`${apiBase}/api/auth/wechat`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code, redirect_uri: redirectUri, keep, keepDays }),
    })
    if (!res.ok) {
      throw new Error('wechat_login_failed')
    }
    const data = (await res.json()) as {
      token: string
      refreshToken: string
      email: string
      username: string
      nickname: string
      avatar: string
      createdAt: string
      isSubscribed: boolean
    }
    const nextSession: AuthSession = {
      token: data.token,
      refreshToken: data.refreshToken,
      email: data.email,
      username: data.username,
      nickname: data.nickname,
      avatar: data.avatar,
      createdAt: data.createdAt,
      isSubscribed: data.isSubscribed,
      ...buildKeepFields(keep, keepDays),
    }
    setSession(nextSession)
    if (postOAuthPopupSuccess('wechat', nextSession)) {
      message.value = 'WeChat login succeeded. Closing...'
      globalThis.window.setTimeout(() => globalThis.window.close(), 300)
      return
    }
    await finishWechatLoginSuccess()
  } catch {
    message.value = 'WeChat login failed. Please close this window and try again.'
    postOAuthPopupFailure('wechat', message.value)
  }
})
</script>

<style scoped>
.ed-wx-cb {
  min-height: 40vh;
  display: grid;
  place-items: center;
  padding: 2rem;
}

.ed-wx-cb__msg {
  margin: 0;
  font-size: 0.95rem;
  color: #334155;
}
</style>
